import {Component, DestroyRef, Inject, inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {DialogTextModel} from "../../../core/cms/models/dialog.text.model";
import {DialogRestService} from "../../../core/cms/services/dialog.rest.service";
import {finalize, map, switchMap, take} from "rxjs/operators";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommunicationRestService} from "../../../core/communication/api/communication.rest-service";
import {LogService} from "../../../core/base/log.service";
import {Router} from '@angular/router';

interface ParsedBodySegment {
  data: string;
  type: 'string' | 'form' | 'email';
  email?: string;
}

@Component({
  selector: 'app-communication',
  templateUrl: './communication.component.html',
  styleUrl: './communication.component.scss',
  standalone: false
})
export class CommunicationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly log = inject(LogService);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  public parsedBody: ParsedBodySegment[] = [];
  public suggestedEmail?: string;
  loading = true;

  private readonly TAG_PATTERN = /\[form=([0-9]*)]|\[email=([a-zA-Z,_]+)]/g;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogTextModel,
              private dialogRestService: DialogRestService,
              public dialogRef: MatDialogRef<CommunicationComponent>,
              public communicationService: CommunicationRestService,
              public router: Router) {
  }

  ngOnInit(): void {
          this.parsedBody = this.parseBody(this.data.body);
          this.suggestedEmail = this.parsedBody.find((part) => part.type === 'email')?.email;
          this.loading = false;
  }

  private parseBody(body: string | undefined): ParsedBodySegment[] {
    if (!body) {
      return [];
    }

    const segments: ParsedBodySegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    this.TAG_PATTERN.lastIndex = 0;

    while ((match = this.TAG_PATTERN.exec(body)) !== null) {
      const [full, formId, emailValue] = match;

      if (match.index > lastIndex) {
        segments.push({ data: body.slice(lastIndex, match.index), type: 'string' });
      }

      if (formId !== undefined) {
        segments.push({ data: formId.trim(), type: 'form' });
      } else if (emailValue !== undefined) {
        segments.push({ data: emailValue.trim(), email: this.data.email, type: 'email' });
      }

      lastIndex = match.index + full.length;
    }

    if (lastIndex < body.length) {
      segments.push({ data: body.slice(lastIndex), type: 'string' });
    }

    return segments;
  }

  onRedirect() {
    this.dialogRef.close();
    this.router.navigate([this.data.link]);
  }

  onSubscribe() {
    this.submit(false);
  }

  onUseSuggestedAddress() {
    if (!this.suggestedEmail) {
      return;
    }

    this.submit(true);
  }

  private submit(saveConfig: boolean): void {
    if (this.saving()) return;
    if (!this.data.idf) {
      this.error.set('Es wurde keine Apotheke ausgewählt. Bitte öffnen Sie das Fenster erneut.');
      this.log.error('Cannot save communication popup without an IDF');
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const disableClose = this.dialogRef.disableClose;
    this.dialogRef.disableClose = true;
    const subscribe = () => this.dialogRestService.subscribeToDialogOffer(this.data.id, this.data.idf).pipe(
      take(1),
      map(response => {
        if (response.error_code !== null) throw new Error(`Unable to subscribe: ${response.error_code}`);
      })
    );
    const request = saveConfig
      ? this.communicationService.saveConfig(this.data.idf, {
        type: 'TRACK_TRACE', active: true, emails: [this.suggestedEmail]
      }).pipe(
        take(1),
        switchMap(response => {
          if (response.error_code !== null) throw new Error(`Unable to save communication settings: ${response.error_code}`);
          return subscribe();
        })
      )
      : subscribe();
    request.pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.saving.set(false);
        this.dialogRef.disableClose = disableClose;
      })
    ).subscribe({
      next: () => this.dialogRef.close(),
      error: error => {
        this.error.set('Die Einstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.');
        this.log.error('Unable to complete communication popup', error);
      }
    });
  }

}
